"use client";

import { useEffect, useMemo, useState } from "react";
import Head from "next/head";
import CourseCard, { sortCoursesByAvailability, type Course } from "@/components/CourseCard";
import RegisterModal from "@/components/RegisterModal";
import ModalityCard from "@/components/ModalityCard";
import {
  ArrowUpRight,
  BookOpen,
  Compass,
  MapPin,
  Phone,
  Search,
  Sparkles,
  Target,
} from "lucide-react";

type Center = {
  id: string;
  name: string;
  address: string;
  zip: string;
  phone: string;
  imageUrl?: string;
  mapEmbedUrl?: string;
};

export default function Home() {
  const [courses, setCourses] = useState<Course[]>([]);
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterLocation, setFilterLocation] = useState("");
  const [centers, setCenters] = useState<Center[]>([]);

  useEffect(() => {
    async function fetchCourses() {
      const res = await fetch("/api/courses");
      const data = await res.json();
      setCourses(data);
    }
    fetchCourses();
  }, []);

  useEffect(() => {
    async function fetchCenters() {
      const res = await fetch("/api/centers");
      const data = await res.json();
      setCenters(data);
    }
    fetchCenters();
  }, []);

  const publicCourses = useMemo(() => courses.filter((course) => !course.hidden), [courses]);
  const categories = useMemo(
    () => Array.from(new Set(publicCourses.map((course) => course.category))).sort(),
    [publicCourses]
  );
  const locations = useMemo(
    () => Array.from(new Set(publicCourses.map((course) => course.location))).sort(),
    [publicCourses]
  );

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filteredCourses = useMemo(() => {
    return publicCourses.filter((course) => {
      const matchesSearch =
        normalizedSearch === "" ||
        `${course.title} ${course.category} ${course.shortDescription}`.toLowerCase().includes(normalizedSearch);
      const matchesCategory = !filterCategory || course.category === filterCategory;
      const matchesLocation = !filterLocation || course.location === filterLocation;
      return matchesSearch && matchesCategory && matchesLocation;
    });
  }, [publicCourses, normalizedSearch, filterCategory, filterLocation]);

  const sortedCourses = useMemo(() => {
    return sortCoursesByAvailability(filteredCourses);
  }, [filteredCourses]);

  return (
    <>
      <Head>
        <title>Grupo Alpaka - Formación flexible</title>
        <meta
          name="description"
          content="Cursos profesionales de Grupo Alpaka para aprender con flexibilidad."
        />
      </Head>

      <main>
        <section className="alpaka-hero flex items-center">
          <div className="container py-20">
            <div className="max-w-2xl">
              <span className="alpaka-label">
                <Sparkles className="h-3.5 w-3.5 text-[var(--primary)]" />
                Aprendizaje con ritmo propio
              </span>
              <h1 className="mt-6 text-5xl font-black leading-[0.95] tracking-tight text-[var(--ink)] sm:text-6xl lg:text-7xl">
                Grupo Alpaka
              </h1>
              <p className="mt-6 text-lg leading-8 text-slate-700 sm:text-xl">
                Cursos cercanos y pensados para que puedas avanzar sin perder de vista tu día a día.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <a href="#cursos" className="btn-primary">
                  Explorar cursos <ArrowUpRight className="h-4 w-4" />
                </a>
                <a href="#centros" className="btn-secondary">
                  Ubicaciones <MapPin className="h-4 w-4" />
                </a>
              </div>
            </div>

            <div className="mt-12 grid max-w-3xl gap-3 sm:grid-cols-3">
              <div className="alpaka-panel alpaka-dash">
                <p className="text-3xl font-black text-[var(--primary)]">{publicCourses.length || "..."}</p>
                <p className="mt-1 text-sm font-semibold text-slate-700">convocatorias</p>
              </div>
              <div className="alpaka-panel alpaka-dash">
                <p className="text-3xl font-black text-teal-700">{categories.length || "..."}</p>
                <p className="mt-1 text-sm font-semibold text-slate-700">áreas formativas</p>
              </div>
              <div className="alpaka-panel alpaka-dash">
                <p className="text-3xl font-black text-[var(--accent)]">{centers.length || "..."}</p>
                <p className="mt-1 text-sm font-semibold text-slate-700">centros</p>
              </div>
            </div>
          </div>
        </section>

        <section className="alpaka-ribbon">
          <div className="container grid gap-4 py-5 text-sm font-semibold md:grid-cols-3">
            <p className="flex items-center gap-2"><Target className="h-4 w-4 text-[var(--accent)]" /> Cursos útiles desde el primer día</p>
            <p className="flex items-center gap-2"><Compass className="h-4 w-4 text-[var(--accent)]" /> Itinerarios sencillos de seguir</p>
            <p className="flex items-center gap-2"><BookOpen className="h-4 w-4 text-[var(--accent)]" /> Inscripción rápida y acompañada</p>
          </div>
        </section>

        <section id="modalidades" className="section-spacing">
          <div className="container">
            <div className="mx-auto max-w-3xl text-center">
              <span className="alpaka-label">Rutas</span>
              <h2 className="mt-4 text-4xl font-black tracking-tight text-[var(--ink)]">
                Escoge el formato que te encaja
              </h2>
              <p className="mt-4 text-lg leading-8 text-slate-600">
                Presencial, online o por isla. Cada ruta está pensada para ayudarte a encontrar rápido lo que necesitas.
              </p>
            </div>

            <div className="mt-10 grid gap-5 md:grid-cols-3">
              <ModalityCard
                title="Las Palmas"
                description="Cursos presenciales en Gran Canaria con acceso directo a convocatorias activas."
                link="/islas/las-palmas-de-gran-canaria"
                imageUrl="https://images.pexels.com/photos/3184306/pexels-photo-3184306.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1"
              />
              <ModalityCard
                title="Tenerife"
                description="Formación en Tenerife para perfiles que quieren mejorar su empleabilidad."
                link="/islas/santa-cruz-de-tenerife"
                imageUrl="https://images.pexels.com/photos/5428003/pexels-photo-5428003.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1"
              />
              <ModalityCard
                title="Online"
                description="Aprende desde cualquier lugar con propuestas flexibles."
                link="/islas/online"
                imageUrl="https://images.pexels.com/photos/4144222/pexels-photo-4144222.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1"
              />
            </div>
          </div>
        </section>

        <section id="cursos" className="section-spacing bg-[var(--soft)]">
          <div className="container">
            <div className="grid gap-8 lg:grid-cols-[0.8fr,1.2fr] lg:items-end">
              <div>
                <span className="alpaka-label">Cursos</span>
                <h2 className="mt-4 text-4xl font-black tracking-tight text-[var(--ink)]">
                  Encuentra una convocatoria abierta
                </h2>
                <p className="mt-4 text-lg leading-8 text-slate-600">
                  Busca por nombre, categoría o ubicación. Cuando algo encaje, abre el formulario y te contactamos.
                </p>
              </div>
              <div className="alpaka-panel">
                <div className="grid gap-3 md:grid-cols-3">
                  <input
                    type="text"
                    placeholder="Buscar curso"
                    className="input-field md:col-span-3"
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                    }}
                  />
                  <select
                    className="input-field"
                    value={filterCategory}
                    onChange={(e) => {
                      setFilterCategory(e.target.value);
                    }}
                  >
                    <option value="">Categorías</option>
                    {categories.map((category) => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>
                  <select
                    className="input-field"
                    value={filterLocation}
                    onChange={(e) => {
                      setFilterLocation(e.target.value);
                    }}
                  >
                    <option value="">Lugares</option>
                    {locations.map((location) => (
                      <option key={location} value={location}>
                        {location}
                      </option>
                    ))}
                  </select>
                  <button
                    className="btn-secondary"
                    onClick={() => {
                      setSearchTerm("");
                      setFilterCategory("");
                      setFilterLocation("");
                    }}
                  >
                    Limpiar
                  </button>
                </div>
                <p className="mt-4 text-sm font-bold text-teal-900">
                  {filteredCourses.length} resultado{filteredCourses.length !== 1 ? "s" : ""}
                </p>
              </div>
            </div>

            {filteredCourses.length === 0 ? (
              <div className="mt-10 card text-center">
                <Search className="mx-auto h-10 w-10 text-[var(--primary)]" />
                <h3 className="mt-4 text-xl font-black text-[var(--ink)]">
                  Próximamente puede que este curso esté disponible
                </h3>
                <p className="mt-2 text-slate-600">
                  Publicaremos nuevas convocatorias en cuanto estén listas.
                </p>
              </div>
            ) : (
              <>
                <div className="grid-courses mt-10">
                  {sortedCourses.map((course) => (
                    <CourseCard key={course.id} course={course} onRegister={setSelectedCourse} />
                  ))}
                </div>
              </>
            )}
          </div>
        </section>

        <section id="centros" className="section-spacing">
          <div className="container">
            <div className="mx-auto max-w-3xl text-center">
              <span className="alpaka-label">Centros</span>
              <h2 className="mt-4 text-4xl font-black tracking-tight text-[var(--ink)]">Dónde encontrarnos</h2>
              <p className="mt-4 text-lg text-slate-600">
                Centros y puntos de atención para resolver dudas y acompañarte durante la inscripción.
              </p>
            </div>

            <div className="mt-10 grid gap-5 md:grid-cols-2">
              {centers.map((center) => (
                <article key={center.id} className="card overflow-hidden p-0">
                  <div className="p-5">
                    <h3 className="text-2xl font-black text-[var(--ink)]">{center.name}</h3>
                    <p className="mt-2 text-slate-600">{center.address}</p>
                    <p className="text-slate-600">{center.zip}</p>
                    {center.phone ? (
                      <p className="mt-3 flex items-center gap-2 font-bold text-teal-900">
                        <Phone className="h-4 w-4 text-[var(--primary)]" />
                        {center.phone}
                      </p>
                    ) : null}
                  </div>
                  <div className={"h-56 border-t border-[var(--line)] " + (center.imageUrl || center.mapEmbedUrl ? "relative overflow-hidden" : "flex items-center justify-center bg-[var(--soft)]")}>
                    {center.mapEmbedUrl ? (
                      <iframe
                        src={center.mapEmbedUrl}
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                        className="absolute inset-0 h-full w-full"
                      />
                    ) : center.imageUrl ? (
                      <img src={encodeURI(center.imageUrl)} alt={center.name} className="absolute inset-0 h-full w-full object-cover" />
                    ) : (
                      <MapPin className="h-14 w-14 text-teal-700/30" />
                    )}
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      </main>

      <RegisterModal course={selectedCourse} onClose={() => setSelectedCourse(null)} />
    </>
  );
}
